import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const HistoricalComparison = ({ auditData }) => {
  const [selectedComparison, setSelectedComparison] = useState('quarter');

  const historicalData = [
    {
      period: 'Q4 2025 (Current)',
      totalQuestions: 45,
      completionRate: 78,
      averageScore: 87,
      participantCount: 45,
      timeToComplete: 6.5,
      isCurrent: true
    },
    {
      period: 'Q3 2025',
      totalQuestions: 42,
      completionRate: 85,
      averageScore: 84,
      participantCount: 48,
      timeToComplete: 7.2,
      isCurrent: false
    },
    {
      period: 'Q2 2025',
      totalQuestions: 38,
      completionRate: 92,
      averageScore: 81,
      participantCount: 45,
      timeToComplete: 5.8,
      isCurrent: false
    },
    {
      period: 'Q1 2025',
      totalQuestions: 40,
      completionRate: 88,
      averageScore: 79,
      participantCount: 42,
      timeToComplete: 6.9,
      isCurrent: false
    }
  ];

  const categoryTrends = [
    {
      category: 'General Safety',
      q1: 82,
      q2: 85,
      q3: 88,
      q4: 92,
      trend: 'up'
    },
    {
      category: 'Fire Safety',
      q1: 78,
      q2: 80,
      q3: 82,
      q4: 85,
      trend: 'up'
    },
    {
      category: 'Emergency Procedures',
      q1: 75,
      q2: 79,
      q3: 77,
      q4: 81,
      trend: 'up'
    },
    {
      category: 'PPE Compliance',
      q1: 85,
      q2: 87,
      q3: 90,
      q4: 88,
      trend: 'down'
    },
    {
      category: 'Equipment Safety',
      q1: 72,
      q2: 75,
      q3: 73,
      q4: 79,
      trend: 'up'
    }
  ];

  const getTrendColor = (trend) => {
    return trend === 'up' ? 'text-green-500' : 'text-orange-500';
  };

  const getTrendIcon = (trend) => {
    return trend === 'up' ? 'TrendingUp' : 'TrendingDown';
  };

  const getChangeIndicator = (current, previous) => {
    const change = current - previous;
    const isPositive = change >= 0;
    return (
      <span className={`text-xs ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? '+' : ''}{change?.toFixed(1)}%
              </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Comparison Controls */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">Compare with:</span>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedComparison('quarter')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedComparison === 'quarter' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              Previous Quarter
            </button>
            <button
              onClick={() => setSelectedComparison('year')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedComparison === 'year' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              Year-over-Year
            </button>
          </div>
        </div>
      </div>
      {/* Historical Performance Table */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Historical Performance Overview</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Period</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Completion Rate</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Avg. Score</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Participants</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Avg. Time (days)</th>
              </tr>
            </thead>
            <tbody>
              {historicalData?.map((data, index) => (
                <tr 
                  key={index}
                  className={`border-b border-border ${data?.isCurrent ? 'bg-primary/5' : ''}`}
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground">{data?.period}</span>
                      {data?.isCurrent && (
                        <span className="px-2 py-0.5 bg-primary text-primary-foreground rounded text-xs">
                          Current
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="text-center py-3 px-4">
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-sm font-medium text-foreground">{data?.completionRate}%</span>
                      {!data?.isCurrent && index > 0 && (
                        getChangeIndicator(historicalData?.[index - 1]?.completionRate, data?.completionRate)
                      )}
                    </div>
                  </td>
                  <td className="text-center py-3 px-4">
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-sm font-medium text-foreground">{data?.averageScore}%</span>
                      {!data?.isCurrent && index > 0 && (
                        getChangeIndicator(historicalData?.[index - 1]?.averageScore, data?.averageScore)
                      )}
                    </div>
                  </td>
                  <td className="text-center py-3 px-4 text-sm text-foreground">{data?.participantCount}</td>
                  <td className="text-center py-3 px-4 text-sm text-foreground">{data?.timeToComplete}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Category Trends */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Category Performance Trends</h3>
        <div className="space-y-4">
          {categoryTrends?.map((category, index) => (
            <div key={index} className="border-b border-border last:border-0 pb-4 last:pb-0">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{category?.category}</span>
                  <Icon 
                    name={getTrendIcon(category?.trend)} 
                    size={16} 
                    className={getTrendColor(category?.trend)} 
                  />
                </div>
                <span className="text-sm font-bold text-foreground">{category?.q4}%</span>
              </div>
              
              <div className="grid grid-cols-4 gap-2">
                <div className="text-center">
                  <div className="text-xs text-muted-foreground mb-1">Q1 2025</div>
                  <div className="text-sm font-medium text-foreground">{category?.q1}%</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-muted-foreground mb-1">Q2 2025</div>
                  <div className="text-sm font-medium text-foreground">{category?.q2}%</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-muted-foreground mb-1">Q3 2025</div>
                  <div className="text-sm font-medium text-foreground">{category?.q3}%</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-muted-foreground mb-1">Q4 2025</div>
                  <div className="text-sm font-medium text-primary">{category?.q4}%</div>
                </div>
              </div>

              <div className="mt-2">
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${category?.q4}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Key Insights */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Key Insights</h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-green-500/10 rounded-lg">
            <Icon name="TrendingUp" size={20} className="text-green-500 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-foreground mb-1">Improving Trend</div>
              <div className="text-xs text-muted-foreground">
                Overall safety scores have improved by 8% over the past year, with General Safety showing the strongest growth.
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-orange-500/10 rounded-lg">
            <Icon name="AlertCircle" size={20} className="text-orange-500 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-foreground mb-1">Area for Improvement</div>
              <div className="text-xs text-muted-foreground">
                PPE Compliance showed a slight decrease in Q4. Consider additional training or awareness programs.
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-blue-500/10 rounded-lg">
            <Icon name="Info" size={20} className="text-blue-500 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-foreground mb-1">Participation Note</div>
              <div className="text-xs text-muted-foreground">
                Response rates are 7% lower than Q3. Consider sending reminders earlier in the audit period.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoricalComparison;